<template>
  <div
    v-show="products?.length"
    class="complete-the-look critical-component-clear"
  >
    <div class="complete-the-look__header">
      <h2
        class="
          complete-the-look__header-title
          text-desktop-small-desktop
          text-mobile-regular-uc-mobile
        "
        v-text="$string('product.complete_the_look.title')"
      />
    </div>

    <div
      v-if="products?.length"
      class="complete-the-look__grid"
    >
      <div
        v-for="(product, index) of products"
        :key="`complete-the-look-column-${index}`"
        class="complete-the-look__column"
        data-gtm-event="complete-the-look"
      >
        <product-card
          :product="product"
          :show-badge="section.showBadges"
          :show-flat-image="section.showFlatImages"
          disable-image-hover
          :show-variants="false"
          hide-color-swatches
          :mobile-gallery="false"
          block-class="complete-the-look"
        />
      </div>
    </div>

    <div
      v-if="products?.length"
      ref="carouselContainer"
      class="
        complete-the-look__carousel
        splide
      "
      :js-carousel="`ctl-${namespace}`"
    >
      <div class="splide__track">
        <ul class="splide__list">
          <li
            v-for="(product, index) of products"
            :key="`complete-the-look-slide-${index}`"
            class="splide__slide"
          >
            <product-card
              layout="small"
              :product="product"
              :show-badge="section.showBadges"
              :show-flat-image="section.showFlatImages"
              :show-variants="false"
              :mobile-gallery="false"
            />
          </li>
        </ul>
      </div>
    </div>

    <secondary-product-data
      context="curated"
      @products-updated="handleProductsUpdate"
    />
  </div>
</template>

<script>

/**
 * Vue: Complete the look (complete-the-look)
 * -----------------------------------------------------------------------------
 * Curated product recommendations driven by tags.
 *
 * @param {String} namespace - Section ID for generating unique carousel
 * namespace.
 * @param {Object} section - Section settings.
 *
 */
import { mapState } from 'vuex'
import { nextTick } from 'vue'

import SecondaryProductData from '~async/main-product/secondary-product-data'
import ProductCard from '~async/product-card/product-card'

import Carousel from '~/api/carousel'

export default {
  name: 'CompleteTheLook',

  components: {
    ProductCard,
    SecondaryProductData,
  },

  props: {
    namespace: {
      type: String,
      default: null,
      required: true,
    },
    section: {
      type: Object,
      default: () => ({
        enableQuickAdd: false,
        showBadges: false,
        showFlatImages: true,
        products: [],
      }),
    },
  },

  emits: {
    'complete-the-look-loaded': (payload) => typeof payload === 'boolean',
  },

  data() {
    return {
      products: [],
    }
  },
  computed: {

    /**
     * Map Vuex state.
     */
    ...mapState({
      isMobile: (state) => state.index.screen.mobile,
    }),

    /**
     * Grabs the current namespace.
     * @returns {String}
     */
    carouselNamespace() {
      return this.namespace
        ? `[js-carousel="ctl-${this.namespace}"]`
        : false
    },
  },

  watch: {

    /**
     * When the products array updates, destroy any existing carousels and
     * build a new one.
     */
    products: {
      immediate: true,
      handler(products) {
        if (this.carousel) {
          this.carousel.destroy()
        }

        if (!products?.length) {
          return
        }
        this.createCarousel()
      },
    },
  },

  methods: {

    /**
     * Returns a value depending on the screen size.
     * The value indicates the position relative to the top of the viewport.
     * @returns {String}
     */
    checkSize() {
      return this.isMobile ? '120' : '160'
    },

    /**
     * Handles products being updated by the secondary product data component.
     * @param {Array} products - Array of product objects.
     */
    handleProductsUpdate(products) {
      if (!products) {
        this.products = []
        return
      }

      this.products = [...products]
    },

    /**
     * Main carousel contructor function
     */
    createCarousel() {
      if (!this.namespace) {
        return
      }

      nextTick().then(() => {
        const container = this.$refs.carouselContainer

        if (!container) {
          return
        }

        this.carousel = Carousel(this.carouselNamespace, {
          type: 'slide',
          start: Math.round((this.products.length / 2) - 1),
          focus: 'center',
          padding: { left: 'var(--spacing-4)', right: 'var(--spacing-4)' },
          rewind: true,
          autoplay: false,
        })

        this.carousel.mount()
        this.$emit('complete-the-look-loaded', true)
      })
    },
  },
}
</script>

<style lang="scss">
@import './complete-the-look';
</style>
